import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, ExternalLink } from "lucide-react";
import { BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, dec, num } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";

const kvm = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} m²`);
const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const m2 = (v) => (v == null ? "—" : `${dec(v, 2)} m²`);

const Kort = ({ tittel, verdi, under, ekstra, testId, farge = "text-[#1F2A36]" }) => (
  <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={testId}>
    <p className="text-xs text-[#8AA4BF]">{tittel}</p>
    <p className={`text-2xl font-light ${farge}`}>{verdi}</p>
    {under && <p className="text-[11px] text-[#4B647F]">{under}</p>}
    {ekstra && <p className="text-[11px] text-[#3F8A86] mt-1">{ekstra}</p>}
  </div>
);

const Rangering = ({ rang, tittel, format, testId, gruppeOrd = "fylket" }) => {
  if (!rang) return null;
  return (
    <p className="text-xs text-[#4B647F]" data-testid={testId}>
      {tittel}: nr. <span className="font-semibold text-[#103766]">{rang.own_rank}</span> av {rang.count} i {gruppeOrd} (høgast:{" "}
      {rang.hoegast.name} {format(rang.hoegast.value)} · lågast: {rang.lagast.name} {format(rang.lagast.value)} · median {format(rang.median)})
    </p>
  );
};

export const EiendomSection = ({ data }) => {
  const latest = data?.latest;
  if (!latest) return null;
  const { history = [], fylke, land, bygningstypar = [], etterslep, rangering = {}, fylke_name } = data;
  const gruppe = data.gruppe || fylke;
  const gruppeNamn = data.gruppe_namn || fylke_name;
  const gruppeLabel = data.gruppe_namn ? "Storbyane" : "Fylket";
  const utanFylke = !gruppe;
  const gruppeOrd = data.gruppe_namn ? "storbygruppa" : "fylket";
  const fylkeKey = data.gruppe_namn ? "Snitt for storbyane" : `Fylkessnitt (${gruppeNamn || "fylket"})`;
  const snitt = (verdi, landverdi) => (utanFylke ? `Landet: ${landverdi}` : `${gruppeLabel}: ${verdi} · Landet: ${landverdi}`);

  const arealChart = history.map((r) => ({
    year: r.year, Eigd: r.areal_eid, Leigd: r.areal_leid, "Per innbyggjar": r.areal_per_innb,
  }));
  const byFylke = Object.fromEntries(((data.gruppe_history || data.fylke_history) || []).map((r) => [r.year, r]));
  const byLand = Object.fromEntries((data.land_history || []).map((r) => [r.year, r]));
  const vedlikehaldChart = history.map((r) => ({
    year: r.year,
    Kommunen: r.vedlikehald_per_kvm,
    ...(utanFylke ? {} : { [fylkeKey]: byFylke[r.year]?.vedlikehald_per_kvm ?? null }),
    Landssnitt: byLand[r.year]?.vedlikehald_per_kvm ?? null,
    "Norm 100 kr (2009)": r.norm_lav,
    "Norm 170 kr (2009)": r.norm_hoeg,
  }));
  const etterslepChart = (etterslep?.rows || []).map((r) => ({
    year: r.year, "Lågt anslag": r.gap_lav_kr, "Høgt anslag": r.gap_hoeg_kr,
  }));
  const typeChart = bygningstypar.map((t) => ({ name: t.label, Eigd: t.eid, Leigd: t.leid, pct: t.pct }));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="eiendom-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#103766]" />
          Eigedomsmassen – kvadratmeter, vedlikehald og etterslep
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kor mange kvadratmeter formålsbygg kommunen eig og leiger, kva ho brukar på vedlikehald, drift, reinhald og energi per kvadratmeter, og
          kor stort vedlikehaldsetterslepet kan vere.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <Kort
            testId="eiendom-areal"
            tittel={`Eigd areal ${latest.year}`}
            verdi={kvm(latest.areal_eid)}
            under={`+ ${kvm(latest.areal_leid)} leigd = ${kvm(latest.areal_total)} i alt`}
          />
          <Kort
            testId="eiendom-per-innb"
            tittel="Areal per innbyggjar"
            verdi={m2(latest.areal_per_innb)}
            under={latest.population ? `${formatNumber(latest.population)} innbyggjarar` : null}
            ekstra={snitt(m2(gruppe?.areal_per_innb), m2(land?.areal_per_innb))}
          />
          <Kort
            testId="eiendom-vedlikehald"
            tittel="Vedlikehald per m²"
            verdi={kr(latest.vedlikehald_per_kvm)}
            under={`${mill(latest.vedlikehald_kr)} i alt`}
            ekstra={snitt(kr(gruppe?.vedlikehald_per_kvm), kr(land?.vedlikehald_per_kvm))}
            farge={latest.vedlikehald_per_kvm != null && latest.norm_lav && latest.vedlikehald_per_kvm < latest.norm_lav ? "text-[#C0392B]" : "text-[#1F2A36]"}
          />
          <Kort
            testId="eiendom-norm"
            tittel={`Normkrav ${latest.year}`}
            verdi={`${formatNumber(latest.norm_lav)}–${formatNumber(latest.norm_hoeg)} kr`}
            under="verdibevarande vedlikehald per m²"
            ekstra={
              latest.gap_lav_per_kvm == null
                ? null
                : `Kommunen manglar ${formatNumber(Math.max(0, latest.gap_lav_per_kvm))}–${formatNumber(Math.max(0, latest.gap_hoeg_per_kvm))} kr per m² i året`
            }
          />
          {etterslep && (
            <Kort
              testId="eiendom-etterslep"
              tittel={`Anslått etterslep ${etterslep.aar_fra}–${etterslep.aar_til}`}
              verdi={`${formatNumber(Math.round(etterslep.lav.total_kr / 1000))}–${formatNumber(Math.round(etterslep.hoeg.total_kr / 1000))} mill. kr`}
              under={`${formatNumber(etterslep.lav.per_innb)}–${formatNumber(etterslep.hoeg.per_innb)} kr per innbyggjar`}
              ekstra={`${formatNumber(etterslep.lav.per_kvm)}–${formatNumber(etterslep.hoeg.per_kvm)} kr per m²`}
              farge="text-[#C0392B]"
            />
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kort testId="eiendom-drift" tittel="Driftsutgifter per m²" verdi={kr(latest.drift_per_kvm)} ekstra={`Landet: ${kr(land?.drift_per_kvm)}`} />
          <Kort testId="eiendom-renhald" tittel="Reinhald per m²" verdi={kr(latest.renhald_per_kvm)} ekstra={`Landet: ${kr(land?.renhald_per_kvm)}`} />
          <Kort testId="eiendom-energi" tittel="Energi per m²" verdi={kr(latest.energi_per_kvm)} ekstra={`Landet: ${kr(land?.energi_per_kvm)}`} />
          <Kort
            testId="eiendom-andel"
            tittel="Del av driftsutgiftene"
            verdi={latest.eigedom_pct_av_utgifter == null ? "—" : `${dec(latest.eigedom_pct_av_utgifter, 1)} %`}
            under="netto til eigedomsforvaltning"
            ekstra={`${kr(latest.forvaltning_per_innb)} per innbyggjar`}
          />
        </div>

        <div className="space-y-1">
          <Rangering gruppeOrd={gruppeOrd} rang={rangering.areal_per_innb} tittel="Areal per innbyggjar" format={(v) => `${dec(v, 2)} m²`} testId="eiendom-rang-areal" />
          <Rangering gruppeOrd={gruppeOrd} rang={rangering.vedlikehald_per_kvm} tittel="Vedlikehald per m²" format={(v) => `${formatNumber(v)} kr`} testId="eiendom-rang-vedlikehald" />
          <Rangering gruppeOrd={gruppeOrd} rang={rangering.etterslep_per_innb} tittel="Anslått etterslep per innbyggjar (høgt anslag)" format={(v) => `${formatNumber(v)} kr`} testId="eiendom-rang-etterslep" />
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Kva slags bygg eig kommunen? {latest.year}</p>
          <div className="h-[300px]" data-testid="eiendom-type-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeChart} layout="vertical" barCategoryGap="25%" margin={{ right: 60, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => `${num(v / 1000, 0)}k`} />
                <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={11} width={140} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => kvm(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                <Bar dataKey="Eigd" stackId="a" fill="#103766" radius={[0, 0, 0, 0]}>
                  <LabelList dataKey="pct" position="right" formatter={(v) => `${dec(v, 1)} %`} fontSize={10} fill="#4B647F" />
                </Bar>
                <Bar dataKey="Leigd" stackId="a" fill="#9FB6CC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Areal over tid – eigd og leigd</p>
            <div className="h-[260px]" data-testid="eiendom-areal-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={arealChart} barCategoryGap="30%">
                  {endringsLinjer(arealChart, { yAxisId: "a" })}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis yAxisId="a" stroke="#8AA4BF" fontSize={11} width={54} tickFormatter={(v) => `${num(v / 1000, 0)}k`} />
                  <YAxis yAxisId="p" orientation="right" stroke="#3F8A86" fontSize={11} width={44} tickFormatter={(v) => num(v, 1)} domain={[0, (max) => Math.ceil(max * 1.4)]} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === "Per innbyggjar" ? m2(v) : kvm(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                  <Bar yAxisId="a" dataKey="Eigd" stackId="areal" fill="#103766" />
                  <Bar yAxisId="a" dataKey="Leigd" stackId="areal" fill="#9FB6CC" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="p" type="monotone" dataKey="Per innbyggjar" stroke="#3F8A86" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Vedlikehald per m² mot normkravet</p>
            <div className="h-[260px]" data-testid="eiendom-vedlikehald-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={vedlikehaldChart}>
                  {endringsLinjer(vedlikehaldChart)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={48} tickFormatter={(v) => num(v, 0)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => kr(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                  <Line type="monotone" dataKey="Norm 170 kr (2009)" stroke="#C0392B" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                  <Line type="monotone" dataKey="Norm 100 kr (2009)" stroke="#E8A34A" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                  <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={1.5} dot={false} connectNulls />}
                  <Line type="monotone" dataKey="Landssnitt" stroke="#9FB6CC" strokeWidth={1.5} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {etterslepChart.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
              Kor mykje manglar kvart år? Differansen mellom normkravet og faktisk vedlikehald
            </p>
            <div className="h-[260px]" data-testid="eiendom-etterslep-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={etterslepChart} barCategoryGap="25%">
                  {endringsLinjer(etterslepChart)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={70} tickFormatter={(v) => `${num(v / 1000, 0)} mill.`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => mill(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                  <Bar dataKey="Lågt anslag" fill="#E8A34A" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Høgt anslag" fill="#C0392B" radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {etterslep && (
              <p className="text-xs text-[#4B647F] mt-2" data-testid="eiendom-etterslep-tekst">
                Summerer vi åra {etterslep.aar_fra}–{etterslep.aar_til}, hamnar det anslåtte etterslepet mellom{" "}
                <span className="font-semibold text-[#1F2A36]">{mill(etterslep.lav.total_kr)}</span> og{" "}
                <span className="font-semibold text-[#C0392B]">{mill(etterslep.hoeg.total_kr)}</span> – det vil seie{" "}
                {formatNumber(etterslep.lav.per_kvm)}–{formatNumber(etterslep.hoeg.per_kvm)} kr per kvadratmeter eller{" "}
                {formatNumber(etterslep.lav.per_innb)}–{formatNumber(etterslep.hoeg.per_innb)} kr per innbyggjar. År der kommunen brukar meir enn
                norma trekkjer etterslepet ned igjen.
              </p>
            )}
          </div>
        )}

        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/11906" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            11906 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (areal på kommunale formålsbygg, eigd og leigd),{" "}
          <a href="https://www.ssb.no/statbank/table/12051" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12051 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (nøkkeltal per kvadratmeter) og{" "}
          <a href="https://www.ssb.no/statbank/table/12905" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12905 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (utgifter til forvaltning, drift og vedlikehald). Formålsbygga er administrasjonslokale, barnehagar, skular, institusjonar, idrettsbygg og
          kulturbygg.
        </p>
        <p className="text-xs text-[#8AA4BF]">
          <span className="font-semibold">Slik er etterslepet rekna ut:</span> KS og Multiconsult sin rettleiar for verdibevarande vedlikehald
          opererer med 100 kr per m² i året ved 30 års levetid og 170 kr per m² ved 60 års levetid (inkludert nødvendig oppgradering), i 2009-kroner.
          Normtala er prisjusterte til kvart år med SSB sin{" "}
          <a href="https://www.ssb.no/statbank/table/08655" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            byggjekostnadsindeks for bustadblokk 08655 <ExternalLink className="w-3 h-3" />
          </a>
          , og etterslepet er summen av (norm − faktisk vedlikehald) × areal for åra vi har tal for. Dette er eit anslag, ikkje ei
          tilstandsvurdering: ein kommune kan ha brukt pengane målretta på dei dårlegaste bygga, eller ha bygg som er nye nok til å tole lite
          vedlikehald. Nasjonalt reknar RIF og Multiconsult eit oppgraderingsbehov på kring 168 milliardar kroner for kommunale bygg.
        </p>
      </CardContent>
    </Card>
  );
};

export default EiendomSection;
